import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '../components/ui/alert';
import { resetPassword } from '../lib/api';

const PASSWORD_RULES = [
  'At least 10 characters',
  'An uppercase letter (A–Z)',
  'A lowercase letter (a–z)',
  'A digit (0–9)',
  'A special character (e.g. !@#$%)',
];

export const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(() => searchParams.get('token') || '');
  const [newPwd, setNewPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPwd !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, newPwd);
      toast.success('Password updated. You can sign in now.');
      navigate('/login');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F5F7] flex items-center justify-center p-4">
      <div className="fixed inset-0 grain-overlay" />
      <div className="w-full max-w-md relative z-10 animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl text-[#3F617E] mb-2">Maris Desk</h1>
          <p className="text-stone-500 text-sm">Set a new password</p>
        </div>

        <Card className="border-stone-200 shadow-lg">
          <CardHeader>
            <CardTitle className="font-heading text-2xl text-center">Reset password</CardTitle>
            <CardDescription className="text-center">
              Choose a strong password. The reset link is valid for one hour and single-use.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4 border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-token">Reset token</Label>
                <Input
                  id="reset-token"
                  type="text"
                  placeholder="Paste the reset token"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  required
                  data-testid="reset-token-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reset-pwd">New password</Label>
                <Input
                  id="reset-pwd"
                  type="password"
                  placeholder="At least 10 characters"
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  required
                  data-testid="reset-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reset-confirm">Confirm new password</Label>
                <Input
                  id="reset-confirm"
                  type="password"
                  placeholder="Re-type the password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  data-testid="reset-confirm-input"
                />
              </div>
              <ul className="text-[11.5px] text-stone-500 leading-relaxed pl-5 list-disc">
                {PASSWORD_RULES.map((r) => <li key={r}>{r}</li>)}
              </ul>
              <Button
                type="submit"
                className="w-full bg-[#3F617E] hover:bg-[#345470] rounded-full"
                disabled={loading}
                data-testid="reset-submit-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set new password'}
              </Button>
              <p className="text-xs text-center text-stone-500">
                <Link to="/login" className="text-[#3F617E] hover:underline">
                  Back to sign in
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
